import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
   FormControlLabel,
Checkbox,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../../../components/Header";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { AccountsandFinanceurl, companyManagementUrl } from "../../../../config";
import { allRegions } from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;
const base_url_company = companyManagementUrl.uri;

const CashAccountForm = (props) => {
  const { isEditing = false, data = {}, onClose, onAction } = props;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]); // Dynamic branches for selected region
  const [loading, setLoading] = useState(true);

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch only regions on mount
  useEffect(() => {
    async function fetchInitialData() {
      try {
        const regRes = await graphqlQuery(allRegions, base_url_company);
        setRegions(regRes || []);
      } catch (err) {
        console.error("Error loading regions:", err);
        Swal.fire("Error", "Failed to load regions", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, []);

  // Fetch branches dynamically when region changes
  const handleRegionChange = async (regionId, setFieldValue) => {
    if (!regionId) {
      setBranches([]);
      setFieldValue("branchId", "");
      return;
    }

    try {
      const inputFields = { regionId: parseInt(regionId) };
      const returnFields = ["id", "branchName"];
      const response = await fetchDataEngine(
        "allBranchesByColumnValues",
        "branchModel",
        inputFields,
        returnFields,
        base_url_company
      );

      const branchOptions = response
        ? response.map((b) => ({
            value: b.id,
            label: b.branchName || "Unnamed Branch",
          }))
        : [];

      setBranches(branchOptions);

      // If editing and current branch doesn't belong to new region → clear it
      if (data?.branchId) {
        const valid = branchOptions.find((b) => b.value === data.branchId);
        if (!valid) {
          setFieldValue("branchId", "");
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
      Swal.fire("Error", "Failed to load branches for selected region", "error");
    }
  };

  // On edit: load branches if region is already set
  useEffect(() => {
    if (isEditing && data?.regionId) {
      // Use a dummy setFieldValue since we don't have access inside useEffect
      handleRegionChange(data.regionId, () => {});
    }
  }, [isEditing, data?.regionId]);

  const initialValues = {
    regionId: data?.regionId || "",
    branchId: data?.branchId || "",
    accountName: data?.accountName || "",
    accountDescription: data?.accountDescription || "",
    status: data?.status ?? 1,
    isSaleAccount: data?.isSaleAccount || false
  };

  const validationSchema = yup.object().shape({
    regionId: yup.number().required("Region is required"),
    branchId: yup.number().required("Branch is required"),
    accountName: yup.string().required("Account Name is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (values.isSaleAccount) {
     // Check if another sale account exists in same branch
     const response = await fetchDataEngine(
       "anyCashAccountsByColumnValues",
       "cashAccounts",
       { branchId: parseInt(values.branchId), isSaleAccount: true },
       ["id", "accountName"],
       base_url
     );

     // If creating new or updating to true, only block if another exists
     if (
       response &&
       response.length > 0 &&
       (!isEditing || response[0].id !== data.id)
     ) {
       Swal.fire(
         "Warning",
         `There is already a Sale Account: <b>${response[0].accountName}</b>. Please uncheck it first.`,
        "warning"
       );
       setSubmitting(false);
       return;
     }
   }
      const mutationData = isEditing
        ? {
            id: data.id,
            regionId: parseInt(values.regionId),
            branchId: parseInt(values.branchId),
            accountName: values.accountName,
            accountDescription: values.accountDescription,
            status: values.status,
            isSaleAccount: values.isSaleAccount,
            updatedBy: userId,
          }
        : {
            regionId: parseInt(values.regionId),
            branchId: parseInt(values.branchId),
            accountName: values.accountName,
            accountDescription: values.accountDescription,
            status: values.status,
            isSaleAccount: values.isSaleAccount,
            createdBy: userId,
            companyId,
          };

      await generateAndExecuteMutation(
        isEditing ? "updateCashAccount" : "createCashAccount",
        mutationData,
        isEditing ? "updatedCashAccount" : "cashAccounts",
        base_url
      );

      Swal.fire("Success", `Cash account ${isEditing ? "updated" : "created"} successfully`, "success");
      onAction();
      onClose();
    } catch (error) {
      console.error("Error saving cash account:", error);
      Swal.fire("Error", "Failed to save cash account", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt="20px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Cash Account" : "Create Cash Account"}
        subtitle="Manage cash account details for branches"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Region */}
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Region *</InputLabel>
                <Select
                  name="regionId"
                  value={values.regionId}
                  onChange={(e) => {
                    handleChange(e);
                    handleRegionChange(e.target.value, setFieldValue);
                  }}
                  error={touched.regionId && !!errors.regionId}
                >
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.regionName || "Unknown Region"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Branch - Dynamic */}
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Branch *</InputLabel>
                <Select
                  name="branchId"
                  value={values.branchId}
                  onChange={handleChange}
                  disabled={!values.regionId}
                  error={touched.branchId && !!errors.branchId}
                >
                  {branches.length === 0 ? (
                    <MenuItem disabled>
                      {values.regionId ? "Loading branches..." : "Select region first"}
                    </MenuItem>
                  ) : (
                    branches.map((b) => (
                      <MenuItem key={b.value} value={b.value}>
                        {b.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* Account Name */}
              <TextField
                fullWidth
                label="Account Name *"
                name="accountName"
                value={values.accountName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.accountName && !!errors.accountName}
                helperText={touched.accountName && errors.accountName}
                sx={{ gridColumn: "span 2" }}
              />
              <FormControlLabel
                sx={{ gridColumn: "span 4" }}
                control={
                    <Checkbox
                    name="isSaleAccount"
                    checked={values.isSaleAccount}
                    onChange={(e) => setFieldValue("isSaleAccount", e.target.checked)}
                    />
                }
                label="Is Default Sale Account"
                />

              {/* Description */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="accountDescription"
                value={values.accountDescription}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditing
                  ? "Update Account"
                  : "Create Account"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CashAccountForm;